import { restClient } from '@/Core'

import type { IHomeOverview, OverviewPeriod } from '@/Ship/Contracts/IHomeOverview'

/** Ендпойнт огляду «Робочого столу». Власник — `ReportContainer` бека. */
export const HOME_OVERVIEW_URL = '/services/reports/overview'

type Raw = Record<string, unknown>

const obj = (value: unknown): Raw =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Raw) : {}

const list = (value: unknown): Raw[] => (Array.isArray(value) ? value.map(obj) : [])

const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0)

const share = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

const str = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null)

const kpiWindow = (value: unknown): IHomeOverview['kpi']['current'] => {
    const raw = obj(value)

    return {
        created: num(raw['created']),
        closed: num(raw['closed']),
        turnover: num(raw['turnover']),
        profit: num(raw['profit']),
        margin: share(raw['margin']),
        onTime: share(raw['onTime']),
    }
}

const funnel = (value: unknown): IHomeOverview['sources']['request'] => {
    const raw = obj(value)

    return {
        total: num(raw['total']),
        new: num(raw['new']),
        priced: num(raw['priced']),
        won: num(raw['won']),
        lost: num(raw['lost']),
    }
}

/**
 * Огляд одним запитом: компанія (адміністратор без `userUuid`), обраний
 * співробітник або свої цифри. Відповідь зводиться до контракту поле за
 * полем — бракує поля, буде нуль, а не `undefined` у графіку.
 *
 * `forceRefresh` — як у віджетів перевезень: 30-секундний GET-кеш ядрового
 * клієнта віддав би старі числа після події `counters`. Помилку не ковтає.
 */
export const LoadHomeOverviewTask = async (userUuid: string | null, period: OverviewPeriod): Promise<IHomeOverview> => {
    const response = obj(
        await restClient.get(
            HOME_OVERVIEW_URL,
            { searchParams: { period, ...(userUuid ? { user: userUuid } : {}) } },
            { forceRefresh: true }
        )
    )

    const scope = obj(response['scope'])
    const scopeUser = scope['user'] ? obj(scope['user']) : null
    const period$ = obj(response['period'])
    const kpi = obj(response['kpi'])
    const schedule = obj(response['schedule'])
    const upcoming = obj(response['upcoming'])
    const clients = obj(response['clients'])
    const other = obj(clients['other'])
    const routes = obj(response['routes'])
    const sources = obj(response['sources'])
    const offers = obj(sources['offers'])

    return {
        scope: {
            kind: scope['kind'] === 'company' ? 'company' : 'employee',
            user: scopeUser ? { uuid: String(scopeUser['uuid'] ?? ''), fullName: str(scopeUser['fullName']) } : null,
        },
        period: {
            preset: (['month', 'quarter', 'year'].includes(String(period$['preset']))
                ? period$['preset']
                : period) as OverviewPeriod,
            from: String(period$['from'] ?? ''),
            to: String(period$['to'] ?? ''),
        },
        kpi: {
            current: kpiWindow(kpi['current']),
            previous: kpiWindow(kpi['previous']),
            currentFrom: String(kpi['currentFrom'] ?? ''),
            previousFrom: String(kpi['previousFrom'] ?? ''),
            previousTo: String(kpi['previousTo'] ?? ''),
        },
        trend: list(response['trend']).map((point) => ({
            year: num(point['year']),
            month: num(point['month']),
            count: num(point['count']),
            turnover: num(point['turnover']),
            profit: num(point['profit']),
            margin: share(point['margin']),
        })),
        pipeline: list(response['pipeline']).map((stage) => ({
            code: String(stage['code'] ?? ''),
            title: String(stage['title'] ?? ''),
            count: num(stage['count']),
        })),
        schedule: {
            loadingToday: num(schedule['loadingToday']),
            loadingTomorrow: num(schedule['loadingTomorrow']),
            unloadingToday: num(schedule['unloadingToday']),
            unloadingTomorrow: num(schedule['unloadingTomorrow']),
            unloadingOverdue: num(schedule['unloadingOverdue']),
        },
        upcoming: {
            items: list(upcoming['items']).map((item) => {
                const status = obj(item['status'])

                return {
                    uuid: String(item['uuid'] ?? ''),
                    client: str(item['client']),
                    kind: item['kind'] === 'unloading' ? 'unloading' : 'loading',
                    date: String(item['date'] ?? ''),
                    from: str(item['from']),
                    to: str(item['to']),
                    status: {
                        code: String(status['code'] ?? ''),
                        title: String(status['title'] ?? ''),
                        color: str(status['color']),
                    },
                }
            }),
            total: num(upcoming['total']),
        },
        leaderboard: Array.isArray(response['leaderboard'])
            ? list(response['leaderboard']).map((row) => {
                  const user = obj(row['user'])

                  return {
                      user: {
                          uuid: String(user['uuid'] ?? ''),
                          fullName: str(user['fullName']),
                          isActive: user['isActive'] !== false,
                      },
                      applications: num(row['applications']),
                      profit: num(row['profit']),
                      turnover: num(row['turnover']),
                      margin: share(row['margin']),
                  }
              })
            : null,
        clients: {
            items: list(clients['items']).map((row) => ({
                uuid: String(row['uuid'] ?? ''),
                title: String(row['title'] ?? ''),
                applications: num(row['applications']),
                profit: num(row['profit']),
            })),
            other: {
                clients: num(other['clients']),
                applications: num(other['applications']),
                profit: num(other['profit']),
            },
            clients: num(clients['clients']),
            profit: num(clients['profit']),
        },
        routes: {
            items: list(routes['items']).map((row) => ({
                from: String(row['from'] ?? ''),
                to: String(row['to'] ?? ''),
                applications: num(row['applications']),
                profit: num(row['profit']),
            })),
            routes: num(routes['routes']),
        },
        sources: {
            request: funnel(sources['request']),
            tender: funnel(sources['tender']),
            offers: {
                request: num(offers['request']),
                tender: num(offers['tender']),
                application: num(offers['application']),
            },
        },
    }
}
